import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  PaginatedTable,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui';
import type { PaginationState } from '../../components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { adminBackofficeApi } from '../../services/adminBackoffice';
import { Loader2 } from 'lucide-react';

interface WebhookEventRow {
  id: string;
  stripeEventId: string;
  eventType: string;
  status: string;
  errorMessage: string | null;
  attempts: number;
  processedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

function statusBadgeVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'processed':
      return 'default';
    case 'failed':
      return 'destructive';
    case 'processing':
      return 'secondary';
    case 'pending':
      return 'outline';
    default:
      return 'outline';
  }
}

export default function WebhookEventsPage() {
  const [events, setEvents] = useState<WebhookEventRow[]>([]);
  const [pagination, setPagination] = useState<PaginationState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  /** Default: newest by created time (matches API defaults). */
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailJson, setDetailJson] = useState<string | null>(null);
  const [detailTitle, setDetailTitle] = useState<string>('');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    adminBackofficeApi
      .getWebhookEvents({
        page,
        limit: 20,
        sortBy,
        sortOrder,
        ...(statusFilter ? { status: statusFilter } : {}),
      })
      .then((data) => {
        if (data.success && data.data) {
          setEvents(data.data.events ?? []);
          setPagination(data.data.pagination ?? null);
        } else setError(data.error || 'Failed to load');
      })
      .catch((err) => setError(err?.response?.data?.error || err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [page, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRetry = async (stripeEventId: string) => {
    setRetryingId(stripeEventId);
    setError(null);
    try {
      const data = await adminBackofficeApi.retryWebhookEvent(stripeEventId);
      if (!data.success) {
        setError(data.error || 'Retry failed');
        return;
      }
      load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err as Error)?.message ||
        'Retry failed';
      setError(msg);
    } finally {
      setRetryingId(null);
    }
  };

  const openDetail = async (stripeEventId: string) => {
    setDetailTitle(stripeEventId);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailJson(null);
    try {
      const data = await adminBackofficeApi.getWebhookEvent(stripeEventId);
      if (data.success && data.data?.event) {
        setDetailJson(JSON.stringify(data.data.event, null, 2));
      } else {
        setDetailJson(JSON.stringify({ error: data.error || 'Failed to load' }, null, 2));
      }
    } catch (err: unknown) {
      setDetailJson(
        JSON.stringify(
          { error: (err as Error)?.message || 'Failed to load' },
          null,
          2,
        ),
      );
    } finally {
      setDetailLoading(false);
    }
  };

  if (error && events.length === 0 && !loading) {
    return (
      <div>
        <PageHeader
          title="Webhook events"
          help={
            <p>
              Stripe webhook deliveries recorded in the database. Failed handlers can be replayed after
              fixing data.
            </p>
          }
        />
        <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Webhook events"
        help={
          <p>
            Stripe webhook deliveries recorded in the database. New deliveries use status
            &quot;processing&quot; then &quot;processed&quot; or &quot;failed&quot;. Failed handlers return 500 to Stripe;
            you can replay a failed event after fixing data.
          </p>
        }
      />

      {error && events.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 p-3 text-sm text-amber-900 dark:text-amber-100">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <h2 className="text-lg font-medium">History</h2>
            <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:gap-4">
              <label className="flex items-center gap-2 text-sm">
                <span className="text-gray-600 dark:text-gray-400 whitespace-nowrap">Status</span>
                <select
                  className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm min-w-[10rem]"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All</option>
                  <option value="failed">Failed</option>
                  <option value="processing">Processing</option>
                  <option value="processed">Processed</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <span className="text-gray-600 dark:text-gray-400 whitespace-nowrap">Sort by</span>
                <select
                  className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm min-w-[11rem]"
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="createdAt">Created</option>
                  <option value="updatedAt">Updated</option>
                  <option value="processedAt">Processed</option>
                  <option value="eventType">Event type</option>
                  <option value="status">Status</option>
                  <option value="attempts">Attempts</option>
                  <option value="stripeEventId">Stripe event ID</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <span className="text-gray-600 dark:text-gray-400 whitespace-nowrap">Order</span>
                <select
                  className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm min-w-[10rem]"
                  value={sortOrder}
                  onChange={(e) => {
                    setSortOrder(e.target.value as 'asc' | 'desc');
                    setPage(1);
                  }}
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <PaginatedTable<WebhookEventRow>
            columns={[
              {
                key: 'type',
                header: 'Event type',
                render: (row) => (
                  <span className="font-mono text-xs break-all">{row.eventType}</span>
                ),
              },
              {
                key: 'stripeId',
                header: 'Stripe event ID',
                render: (row) => (
                  <span className="font-mono text-xs break-all">{row.stripeEventId}</span>
                ),
              },
              {
                key: 'status',
                header: 'Status',
                render: (row) => (
                  <Badge variant={statusBadgeVariant(row.status)}>{row.status}</Badge>
                ),
              },
              { key: 'attempts', header: 'Attempts', render: (row) => row.attempts },
              {
                key: 'error',
                header: 'Last error',
                render: (row) => (
                  <span className="text-xs text-red-600 dark:text-red-400 max-w-xs line-clamp-2">
                    {row.errorMessage ?? '–'}
                  </span>
                ),
              },
              {
                key: 'created',
                header: 'Created',
                render: (row) =>
                  row.createdAt ? new Date(row.createdAt).toLocaleString() : '–',
              },
              {
                key: 'actions',
                header: 'Actions',
                render: (row) => (
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => openDetail(row.stripeEventId)}>
                      Details
                    </Button>
                    {row.status === 'failed' && (
                      <Button
                        type="button"
                        size="sm"
                        disabled={retryingId === row.stripeEventId}
                        onClick={() => handleRetry(row.stripeEventId)}
                      >
                        {retryingId === row.stripeEventId ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin mr-1 inline" />
                            Retrying
                          </>
                        ) : (
                          'Retry'
                        )}
                      </Button>
                    )}
                  </div>
                ),
              },
            ]}
            data={events}
            pagination={pagination}
            loading={loading}
            emptyMessage="No webhook events found."
            onPageChange={setPage}
            getRowKey={(row) => row.id}
          />
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm truncate">{detailTitle}</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto flex-1 min-h-0">
            {detailLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
              </div>
            ) : (
              <pre className="text-xs bg-gray-50 dark:bg-gray-900 p-3 rounded-md overflow-auto max-h-[60vh] whitespace-pre-wrap break-words">
                {detailJson ?? '—'}
              </pre>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
